import React from "react";

import { cn } from "../../../common/u1/cn";
import { Input } from "../../../common/u1/Input";
import { SelectField } from "../../../common/u1/SelectField";
import { CountrySelect } from "../../../common/u1/CountrySelect";
import { EditSectionLayout } from "./EditSectionLayout";
import { EditFooterBar } from "./EditFooterBar";
import { SectionStatusPill } from "./SectionStatusPill";
import { CompanySearchField } from "./CompanySearchField";
import { useAuditorInformation } from "./useAuditorInformation";
import { YEAR_END_HINT } from "./auditorFields";
import { sectionStatus } from "./sectionDrafts";

/* Section 15 — Auditor Information. */
export const AuditorInformationSection = ({
  onNext,
  onPrev,
  onSkip,
  header,
  className = "",
}) => {
  const {
    values,
    setField,
    setCountry,
    chooseFirm,
    states,
    errors,
    busy,
    save,
    status,
  } = useAuditorInformation();

  const onContinue = async () => {
    const saved = await save();
    /* Transcribed with its bug removed: every live branch advances from a
       `finally`, so a failed save moved the viewer on regardless. */
    if (saved) onNext?.();
  };

  return (
    <EditSectionLayout
      className={className}
      header={header}
      footer={
        <EditFooterBar
          onBack={onPrev}
          onSkip={onSkip}
          onContinue={onContinue}
          isLoading={busy}
        />
      }
    >
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="m-0 text-[16px] font-bold text-[var(--ink)] font-sans">
          Auditor Information
        </h2>
        {status && <SectionStatusPill status={sectionStatus({ status })} />}
      </div>

      {/* One card holding the whole form, as the design draws it — there are
          no rows to list here. */}
      <div
        className={cn(
          "mt-3.5 rounded-[14px] border border-[var(--line)] bg-white",
          "p-4 sm:p-6"
        )}
      >
        <CompanySearchField
          label="Audited Firm"
          id="u1-auditor-firm"
          required={false}
          ariaRequired
          fieldName="Audited Firm"
          chipLabel="Firm search"
          placeholder="Search firm…"
          manualPlaceholder="Enter the firm's name"
          notFoundPrompt="Can't find the company you're looking for?"
          addLabel="Add company"
          value={values.firmName}
          onSelect={chooseFirm}
          error={errors.firmName}
        />

        <div className="mt-3.5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {/* Month and day only: the live field is a picker formatted MM/dd,
              and the stored year never reaches the screen. */}
          <Input
            label="Financial Year End"
            labelVariant="field"
            id="u1-auditor-year-end"
            inputMode="numeric"
            autoComplete="off"
            placeholder={YEAR_END_HINT}
            value={values.yearEnd}
            onChange={(e) => setField("yearEnd", e.target.value)}
            error={errors.yearEnd}
          />

          <Input
            label="Last Audited"
            labelVariant="field"
            id="u1-auditor-last-audited"
            inputMode="numeric"
            autoComplete="off"
            maxLength={4}
            placeholder="YYYY"
            value={values.lastAudited}
            onChange={(e) => setField("lastAudited", e.target.value)}
            error={errors.lastAudited}
          />
        </div>

        <div className="mt-3.5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {/* The shared searchable picker, not a 242-option list. */}
          <CountrySelect
            label="Country"
            id="u1-auditor-country"
            labelVariant="field"
            value={values.country}
            onChange={(country) => setCountry(country?.code)}
            error={errors.country}
          />

          {/* A country with a state list gets a select; one without gets a free-text field. */}
          {states.length > 0 ? (
            <SelectField
              label="State"
              labelVariant="field"
              id="u1-auditor-state"
              value={values.state}
              placeholder="Select a state"
              options={states.map((s) => ({ value: s.name, label: s.name }))}
              onChange={(e) => setField("state", e.target.value)}
              error={errors.state}
            />
          ) : (
            <Input
              label="State"
              labelVariant="field"
              id="u1-auditor-state"
              autoComplete="off"
              placeholder={values.country ? "Enter a state" : "Select a country first"}
              disabled={!values.country}
              value={values.state}
              onChange={(e) => setField("state", e.target.value)}
              error={errors.state}
            />
          )}
        </div>
      </div>
    </EditSectionLayout>
  );
};

export default AuditorInformationSection;
